import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { customerService } from '../../services/customerService';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  PriceChange as PriceChangeIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  PersonOff as PersonOffIcon,
  Add as AddIcon
} from '@mui/icons-material';

const WaterBillingSettingsPage = () => {
  const [barangays, setBarangays] = useState([]);
  const [readerAssignments, setReaderAssignments] = useState({});
  const [surchargeSettings, setSurchargeSettings] = useState({
    id: null,
    due_day: '',
    first_surcharge_percent: '',
    second_surcharge_percent: ''
  });
  const [customerTypes, setCustomerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [readerSaving, setReaderSaving] = useState(false);
  const [readerSaved, setReaderSaved] = useState(false);
  const [readerError, setReaderError] = useState(null);
  const [surchargeSaving, setSurchargeSaving] = useState(false);
  const [surchargeSaved, setSurchargeSaved] = useState(false);
  const [surchargeError, setSurchargeError] = useState(null);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [ratesSaved, setRatesSaved] = useState(false);
  const [ratesError, setRatesError] = useState(null);
  const [expandReaders, setExpandReaders] = useState(true);
  const [expandSurcharge, setExpandSurcharge] = useState(false);
  const [expandRates, setExpandRates] = useState(false);
  const [expandNoticeExclusions, setExpandNoticeExclusions] = useState(false);

  const [excludedCustomers, setExcludedCustomers] = useState([]);
  const [excludedLoading, setExcludedLoading] = useState(false);
  const [excludedError, setExcludedError] = useState(null);

  const [excludeSearchQuery, setExcludeSearchQuery] = useState('');
  const [excludeSearchResults, setExcludeSearchResults] = useState([]);
  const [excludeSearchLoading, setExcludeSearchLoading] = useState(false);
  const [excludeSearchError, setExcludeSearchError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const barangayResult = await customerService.getBarangays();
        const customerTypeResult = await customerService.getCustomerTypes();

        if (barangayResult.success) {
          setBarangays(barangayResult.data || []);
        } else {
          setBarangays([]);
        }

        const assignments = {};
        (barangayResult.data || []).forEach(b => {
          const name = b.barangay || b.name || '';
          if (name) {
            assignments[name] = b.reader || '';
          }
        });
        setReaderAssignments(assignments);

        if (customerTypeResult.success) {
          setCustomerTypes(customerTypeResult.data || []);
        } else {
          setCustomerTypes([]);
        }

        try {
          const { data, error } = await supabase
            .from('surcharge_settings')
            .select('*')
            .limit(1)
            .single();

          if (error) {
            throw error;
          }

          setSurchargeSettings({
            id: data.id,
            due_day: data.due_day ?? '',
            first_surcharge_percent: data.first_surcharge_percent ?? '',
            second_surcharge_percent: data.second_surcharge_percent ?? ''
          });
        } catch (e) {
          setSurchargeSettings({
            id: null,
            due_day: 10,
            first_surcharge_percent: 10,
            second_surcharge_percent: 15
          });
        }
      } catch (e) {
        console.error('Error loading water billing settings:', e);
        setLoadError('Failed to load water billing settings.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadExcludedCustomers();
    }
  }, [loading]);

  useEffect(() => {
    const q = excludeSearchQuery.trim();
    if (q.length < 2) {
      setExcludeSearchResults([]);
      setExcludeSearchError(null);
      return;
    }

    const handle = setTimeout(() => {
      searchCustomersToExclude(q);
    }, 300);

    return () => clearTimeout(handle);
  }, [excludeSearchQuery]);

  const loadExcludedCustomers = async () => {
    setExcludedLoading(true);
    setExcludedError(null);
    try {
      const { data: excluded, error: excludedErr } = await supabase
        .from('notice_excluded_customers')
        .select('customerid, created_at')
        .order('created_at', { ascending: false });

      if (excludedErr) throw excludedErr;

      const ids = (excluded || []).map(r => r.customerid).filter(v => v !== null && v !== undefined);
      if (ids.length === 0) {
        setExcludedCustomers([]);
        return;
      }

      const { data: customers, error: customersErr } = await supabase
        .from('customers')
        .select('customerid, name, barangay, type, status')
        .in('customerid', ids);

      if (customersErr) throw customersErr;

      const byId = new Map((customers || []).map(c => [String(c.customerid), c]));
      const merged = (excluded || []).map(r => {
        const c = byId.get(String(r.customerid));
        return {
          customerid: r.customerid,
          name: c?.name || '',
          barangay: c?.barangay || '',
          type: c?.type || '',
          status: c?.status || '',
          created_at: r.created_at
        };
      });

      setExcludedCustomers(merged);
    } catch (err) {
      console.error('Error loading notice excluded customers:', err);
      setExcludedError('Failed to load excluded customers.');
    } finally {
      setExcludedLoading(false);
    }
  };

  const searchCustomersToExclude = async (query) => {
    setExcludeSearchLoading(true);
    setExcludeSearchError(null);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('customerid, name, barangay, type, status')
        .ilike('name', `%${query}%`)
        .order('name', { ascending: true })
        .limit(20);

      if (error) throw error;

      const existing = new Set(excludedCustomers.map(c => String(c.customerid)));
      const results = (data || []).filter(c => !existing.has(String(c.customerid)));
      setExcludeSearchResults(results);
    } catch (err) {
      console.error('Error searching customers for exclusion:', err);
      setExcludeSearchError('Failed to search customers.');
    } finally {
      setExcludeSearchLoading(false);
    }
  };

  const handleAddExcludedCustomer = async (customer) => {
    if (!customer?.customerid) return;
    setExcludedError(null);
    try {
      const { error } = await supabase
        .from('notice_excluded_customers')
        .upsert({ customerid: customer.customerid });

      if (error) throw error;

      setExcludeSearchQuery('');
      setExcludeSearchResults([]);
      await loadExcludedCustomers();
    } catch (err) {
      console.error('Error adding excluded customer:', err);
      setExcludedError(err?.message ? `Failed to exclude customer: ${err.message}` : 'Failed to exclude customer from notices.');
    }
  };

  const handleRemoveExcludedCustomer = async (customerId) => {
    if (customerId === null || customerId === undefined) return;
    setExcludedError(null);
    try {
      const { error } = await supabase
        .from('notice_excluded_customers')
        .delete()
        .eq('customerid', customerId);

      if (error) throw error;
      await loadExcludedCustomers();
    } catch (err) {
      console.error('Error removing excluded customer:', err);
      setExcludedError(err?.message ? `Failed to remove excluded customer: ${err.message}` : 'Failed to remove excluded customer.');
    }
  };

  const handleChangeReader = (barangayName, value) => {
    setReaderAssignments(prev => ({
      ...prev,
      [barangayName]: value
    }));
    setReaderSaved(false);
    setReaderError(null);
  };

  const handleChangeSurcharge = (field, value) => {
    setSurchargeSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSurchargeSaved(false);
    setSurchargeError(null);
  };

  const handleChangeRate = (typeName, field, value) => {
    setCustomerTypes(prev =>
      prev.map(t =>
        t.type === typeName
          ? { ...t, [field]: value }
          : t
      )
    );
    setRatesSaved(false);
    setRatesError(null);
  };

  const handleSaveReaders = async (e) => {
    e.preventDefault();
    setReaderSaving(true);
    setReaderError(null);
    setReaderSaved(false);

    try {
      const updates = Object.entries(readerAssignments).map(([barangayName, reader]) =>
        supabase
          .from('barangays')
          .update({ reader: reader && reader.trim() ? reader.trim() : null })
          .eq('barangay', barangayName)
      );

      const results = await Promise.all(updates);
      const firstError = results.find(r => r.error)?.error;

      if (firstError) {
        console.error('Error saving reader assignments:', firstError);
        setReaderError('Failed to save some reader assignments.');
      } else {
        setReaderSaved(true);
      }
    } catch (err) {
      console.error('Error saving reader assignments:', err);
      setReaderError('Failed to save reader assignments: ' + err.message);
    } finally {
      setReaderSaving(false);
    }
  };

  const handleSaveSurcharge = async (e) => {
    e.preventDefault();
    setSurchargeSaving(true);
    setSurchargeError(null);
    setSurchargeSaved(false);

    try {
      const dueDay = parseInt(surchargeSettings.due_day, 10) || 0;
      const firstPercent = parseFloat(surchargeSettings.first_surcharge_percent) || 0;
      const secondPercent = parseFloat(surchargeSettings.second_surcharge_percent) || 0;

      const { error } = await supabase
        .from('surcharge_settings')
        .upsert({
          id: surchargeSettings.id || 1,
          due_day: dueDay,
          first_surcharge_percent: firstPercent,
          second_surcharge_percent: secondPercent
        });

      if (error) {
        console.error('Error saving surcharge settings:', error);
        setSurchargeError('Failed to save surcharge settings.');
      } else {
        setSurchargeSaved(true);
      }
    } catch (err) {
      console.error('Error saving surcharge settings:', err);
      setSurchargeError('Failed to save surcharge settings: ' + err.message);
    } finally {
      setSurchargeSaving(false);
    }
  };

  const handleSaveRates = async (e) => {
    e.preventDefault();
    setRatesSaving(true);
    setRatesError(null);
    setRatesSaved(false);

    try {
      const updates = customerTypes.map(t => {
        const rate1 = t.rate1 === null || t.rate1 === '' ? null : parseFloat(t.rate1);
        const rate2 = t.rate2 === null || t.rate2 === '' ? null : parseFloat(t.rate2);

        return supabase
          .from('customer_type')
          .upsert({
            type: t.type,
            rate1,
            rate2
          });
      });

      const results = await Promise.all(updates);
      const firstError = results.find(r => r.error)?.error;

      if (firstError) {
        console.error('Error saving customer type rates:', firstError);
        setRatesError('Failed to save some customer type rates.');
      } else {
        setRatesSaved(true);
      }
    } catch (err) {
      console.error('Error saving customer type rates:', err);
      setRatesError('Failed to save customer type rates: ' + err.message);
    } finally {
      setRatesSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ mt: 2, width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Water Billing Settings
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            component="form"
            onSubmit={handleSaveReaders}
            sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    Reader Assignment per Barangay
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assign a meter reader for each barangay. Used in reading sheets and reports.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setExpandReaders(prev => !prev)}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expandReaders ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={expandReaders} timeout="auto" unmountOnExit>
              {readerError && (
                <Alert severity="error" onClose={() => setReaderError(null)} sx={{ mb: 2 }}>
                  {readerError}
                </Alert>
              )}

              {readerSaved && (
                <Alert severity="success" onClose={() => setReaderSaved(false)} sx={{ mb: 2 }}>
                  Reader assignments saved.
                </Alert>
              )}

              <Box sx={{ maxHeight: 320, overflow: 'auto', pr: 1 }}>
                <Grid container spacing={2}>
                  {barangays.map((b) => {
                    const name = b.barangay || b.name || '';
                    const key = name;
                    const value = readerAssignments[key] || '';

                    return (
                      <Grid item xs={12} key={key}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {name}
                          </Typography>
                          <TextField
                            size="small"
                            placeholder="Reader name"
                            value={value}
                            onChange={(e) => handleChangeReader(key, e.target.value)}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={readerSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={readerSaving}
                >
                  Save Reader Settings
                </Button>
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            component="form"
            onSubmit={handleSaveSurcharge}
            sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    Due Date and Penalties
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure the billing due date and surcharge percentages for overdue bills.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setExpandSurcharge(prev => !prev)}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expandSurcharge ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={expandSurcharge} timeout="auto" unmountOnExit>
              {surchargeError && (
                <Alert severity="error" onClose={() => setSurchargeError(null)} sx={{ mb: 2 }}>
                  {surchargeError}
                </Alert>
              )}

              {surchargeSaved && (
                <Alert severity="success" onClose={() => setSurchargeSaved(false)} sx={{ mb: 2 }}>
                  Due date and penalties saved.
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Due Day of Month"
                    type="number"
                    fullWidth
                    size="small"
                    value={surchargeSettings.due_day}
                    onChange={(e) => handleChangeSurcharge('due_day', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="First Surcharge %"
                    type="number"
                    fullWidth
                    size="small"
                    value={surchargeSettings.first_surcharge_percent}
                    onChange={(e) => handleChangeSurcharge('first_surcharge_percent', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Second Surcharge %"
                    type="number"
                    fullWidth
                    size="small"
                    value={surchargeSettings.second_surcharge_percent}
                    onChange={(e) => handleChangeSurcharge('second_surcharge_percent', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ flexGrow: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={surchargeSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={surchargeSaving}
                >
                  Save Penalty Settings
                </Button>
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            component="form"
            onSubmit={handleSaveRates}
            sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PriceChangeIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    Customer Type Rates
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update rate brackets for each customer type.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setExpandRates(prev => !prev)}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expandRates ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={expandRates} timeout="auto" unmountOnExit>
              {ratesError && (
                <Alert severity="error" onClose={() => setRatesError(null)} sx={{ mb: 2 }}>
                  {ratesError}
                </Alert>
              )}

              {ratesSaved && (
                <Alert severity="success" onClose={() => setRatesSaved(false)} sx={{ mb: 2 }}>
                  Customer type rates saved.
                </Alert>
              )}

              <Grid container spacing={2}>
                {customerTypes.map((t) => (
                  <Grid item xs={12} md={4} key={t.type}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t.type}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Rate 1"
                          type="number"
                          size="small"
                          fullWidth
                          value={t.rate1 ?? ''}
                          onChange={(e) => handleChangeRate(t.type, 'rate1', e.target.value)}
                        />
                        <TextField
                          label="Rate 2"
                          type="number"
                          size="small"
                          fullWidth
                          value={t.rate2 ?? ''}
                          onChange={(e) => handleChangeRate(t.type, 'rate2', e.target.value)}
                        />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={ratesSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={ratesSaving}
                >
                  Save Rate Settings
                </Button>
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonOffIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    Notice Exclusions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exclude selected customers from the Notice page list.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setExpandNoticeExclusions(prev => !prev)}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expandNoticeExclusions ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={expandNoticeExclusions} timeout="auto" unmountOnExit>
              {excludedError && (
                <Alert severity="error" onClose={() => setExcludedError(null)} sx={{ mb: 2 }}>
                  {excludedError}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search customer name"
                    placeholder="Type at least 2 characters..."
                    value={excludeSearchQuery}
                    onChange={(e) => setExcludeSearchQuery(e.target.value)}
                    InputProps={{
                      endAdornment: excludeSearchLoading ? <CircularProgress size={18} /> : null
                    }}
                  />
                  {excludeSearchError && (
                    <Typography variant="caption" color="error.main">
                      {excludeSearchError}
                    </Typography>
                  )}

                  {excludeSearchResults.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 1, maxHeight: 260, overflow: 'auto' }}>
                      <List dense>
                        {excludeSearchResults.map((c) => (
                          <ListItem key={c.customerid} divider>
                            <ListItemText
                              primary={c.name}
                              secondary={`${c.customerid} • ${c.barangay || 'N/A'} • ${c.type || 'N/A'} • ${c.status || 'N/A'}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" color="primary" onClick={() => handleAddExcludedCustomer(c)}>
                                <AddIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Excluded Customers
                    </Typography>
                    {excludedLoading && <CircularProgress size={18} />}
                  </Box>

                  <Paper variant="outlined" sx={{ mt: 1, maxHeight: 320, overflow: 'auto' }}>
                    <List dense>
                      {excludedCustomers.length === 0 ? (
                        <ListItem>
                          <ListItemText primary="No excluded customers" />
                        </ListItem>
                      ) : (
                        excludedCustomers.map((c) => (
                          <ListItem key={c.customerid} divider>
                            <ListItemText
                              primary={c.name || String(c.customerid)}
                              secondary={`${c.customerid}${c.barangay ? ` • ${c.barangay}` : ''}${c.type ? ` • ${c.type}` : ''}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" color="error" onClick={() => handleRemoveExcludedCustomer(c.customerid)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                This uses the notice_excluded_customers table in the database.
              </Typography>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WaterBillingSettingsPage;
